import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import FileFormatter from 'components/FileFormatter';
import TagColorFormatter from 'components/TagColorFormatter';
import { GridTable } from 'components/GridTable';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  colors,
  FILE_TYPES,
  JOB_STAGE,
  JOB_STATUS,
  PAGE_PATHS,
  ROLES,
} from '../../constants';
import JobContainer from 'components/styled/JobContainer';
import Notification from 'components/Notification';
import Button from 'components/Button';
import DropDown from 'components/DropDown';
import { BsThreeDots, BsDownload } from 'react-icons/bs';
import { BiTrashAlt, BiCopy } from 'react-icons/bi';
import { useReduxActions } from 'hooks/useReduxActions';
import {
  inquiryValidationErrorsSelector,
  jobSelector,
  userSelector,
} from 'store/selectors';
import {
  ActionBar,
  ActionBarButton,
  ActionBarButtons,
} from 'components/styled/ActionBar';
import Text from 'components/styled/Text';
import { getColumnsWidth } from 'utils/helpers';

const Inquiry = () => {
  const { t } = useTranslation();
  const params = useParams();
  const ref = useRef(null);
  const job = useSelector(jobSelector);
  const user = useSelector(userSelector);
  const inquiryValidationErrors = useSelector(inquiryValidationErrorsSelector);
  const actions = useReduxActions();
  const navigate = useNavigate();

  const [isNotificationShow, setIsNotificationShow] = useState(false);
  const [isDropDownShow, setIsDropDownShow] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isEditable, setIsEditable] = useState(true);

  const columnsWidth = getColumnsWidth();

  const columnDefs = useMemo(
    () => [
      {
        field: 'selected',
        hide: !isEditable,
        pinned: 'left',
        width: columnsWidth.selected,
        headerCheckboxSelection: true,
        checkboxSelection: true,
        showDisabledCheckboxes: true,
        maxWidth: 50,
      },
      {
        headerName: t('inquiry.header.id'),
        field: 'index',
        width: columnsWidth.index,
        pinned: 'left',
        maxWidth: 100,
      },
      {
        headerName: t('inquiry.header.product.name'),
        field: 'product_name',
        width: columnsWidth.product_name,
        pinned: 'left',
        minWidth: 200,
        editable: isEditable,
      },
      {
        headerName: t('inquiry.header.link'),
        field: 'link',
        width: columnsWidth.link,
        type: 'link',
        editable: isEditable,
      },
      {
        headerName: t('inquiry.header.photo.video'),
        field: 'photo_video',
        width: columnsWidth.photo_video,
        minWidth: 200,
        cellRenderer: props => (
          <FileFormatter
            data={props.data.photo_video}
            editable={isEditable}
            type={FILE_TYPES.IMAGE_AND_VIDEO}
            rowId={props.data.row_id}
            recall={reFetch}
            model="job"
          />
        ),
      },
      {
        headerName: t('inquiry.header.color'),
        field: 'color',
        width: columnsWidth.color,
        minWidth: 250,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          overflow: 'visible',
        },
        cellRenderer: props => (
          <TagColorFormatter
            rowIndex={props.rowIndex}
            data={props.data}
            editable={isEditable}
            onChangeRow={handleUpdateJob}
          />
        ),
      },
      {
        headerName: t('inquiry.header.target_price'),
        field: 'target_price',
        width: columnsWidth.target_price,
        minWidth: 200,
        editable: isEditable,
      },
      {
        headerName: t('inquiry.header.quantity'),
        field: 'quantity',
        width: columnsWidth.quantity,
        minWidth: 150,
        editable: isEditable,
      },
      {
        headerName: t('inquiry.header.description'),
        field: 'description',
        width: columnsWidth.description,
        minWidth: 300,
        editable: isEditable,
        cellEditor: 'agLargeTextCellEditor',
        cellEditorPopup: true,
      },
      {
        headerName: t('inquiry.header.documents'),
        field: 'documents',
        width: columnsWidth.documents,
        minWidth: 200,
        cellRenderer: props => (
          <FileFormatter
            data={props.data.documents}
            editable={isEditable}
            type={FILE_TYPES.RAW}
            rowId={props.data.row_id}
            recall={reFetch}
            model="job"
          />
        ),
      },
    ],
    [isEditable], // eslint-disable-line
  );

  const getRowData = () => {
    const rowData = [];
    ref.current.api.forEachNode(node => {
      rowData.push(node.data);
    });
    return rowData;
  };

  const reFetch = () => {
    actions.getJobById(params.ID);
  };

  const addRows = useCallback(
    quantity => {
      const newItems = [...Array(quantity)].map(() => ({ row_id: uuidv4() }));
      actions.addJobInquiryRows(params.ID, newItems);
    },
    [], // eslint-disable-line
  );

  const handleChangeJobs = useCallback(data => {
    actions.changeJobInquiry(params.ID, data);
  }, []); // eslint-disable-line

  const handleSendInquiryButton = () => {
    if (!job.inquiry_payload.some(item => item.product_name)) {
      return;
    }
    actions.sendJobInquiry(params.ID);
    setIsNotificationShow(true);
  };

  // const handleSubmitInquiryButton = async () => {
  //   try {
  //     const res = await actions.submitJobInquiry(params.ID);
  //     if (res) {
  //       navigate(
  //         `/${PAGE_PATHS.CREATE_JOB.ROOT}/${PAGE_PATHS.CREATE_JOB.SEARCH_REPORT}/${params.ID}`,
  //       );
  //     }
  //   } catch (err) {
  //     console.error(err);
  //   }
  // };

  const handleUpdateJob = cell => {
    const preData = getRowData();
    const newData = preData.map(item =>
      item.row_id === cell.row_id ? cell : item,
    );
    actions.updateJobInquiry(params.ID, newData);
  };

  const handleDotsButton = () => {
    setIsDropDownShow(!isDropDownShow);
  };

  const handleClose = () => {
    setIsDropDownShow(false);
  };

  const closeNotification = () => {
    setIsNotificationShow(false);
  };

  const handleSelectItem = useCallback(() => {
    setSelectedRows(ref.current.api.getSelectedRows());
  }, []);

  const handleDeleteRowsButton = () => {
    const ids = selectedRows.map(row => row.row_id);
    actions.deleteJobInquiryRows(params.ID, ids);
  };

  const handleDuplicateButton = () => {
    const copiedRows = [...selectedRows];
    const newItems = copiedRows.map(row => ({
      ...row,
      row_id: uuidv4(),
    }));
    actions.duplicateJobInquiryRows(params.ID, newItems);
  };

  const deleteJobInquiry = async () => {
    try {
      await actions.deleteJobInquiry(params.ID);
      navigate(PAGE_PATHS.JOB);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    setIsEditable(
      !(
        (user.role === ROLES.CLIENT &&
          (job?.stage !== JOB_STAGE.INQUIRY ||
            job?.status[user.role] !== JOB_STATUS.DRAFT)) ||
        user.role === ROLES.SPECIALIST ||
        user.role === ROLES.HELPER
      ),
    );
  }, [job, user]); // eslint-disable-line

  useEffect(() => {
    actions.getJobById(params.ID);
  }, []); // eslint-disable-line

  const dropDownItems = [
    {
      text: t('job.stage.nav.drop.down.item.1'),
      color: colors.navy,
      icon: <BsDownload fill={colors.grey40} />,
    },
    {
      text: t('job.stage.nav.drop.down.item.2'),
      color: colors.red,
      icon: <BiTrashAlt fill={colors.red} />,
      onClick: deleteJobInquiry,
    },
  ];

  if (!job) {
    return (
      <dib>
        <span>loading...</span>
      </dib>
    ); 
  }

  return (
    <>
      {!!selectedRows.length ? (
        <ActionBar>
          <ActionBarButtons>
            <ActionBarButton onClick={handleDuplicateButton}>
              <BiCopy fill={colors.grey40} />
              <Text variant="body" color={colors.navy}>
                {t('inquiry.action.bar.duplicate.button')}
              </Text>
            </ActionBarButton>
          </ActionBarButtons>
          <ActionBarButtons>
            <ActionBarButton onClick={handleDeleteRowsButton}>
              <BiTrashAlt fill={colors.red} />
              <Text variant="body" color={colors.red}>
                {t('inquiry.action.bar.delete.button')}
              </Text>
            </ActionBarButton>
          </ActionBarButtons>
        </ActionBar>
      ) : (
        <JobContainer>
          {job.stage === JOB_STAGE.INQUIRY &&
          job?.status[user.role] === JOB_STATUS.DRAFT ? (
            <>
              <Button
                size="small"
                variant="primary"
                onClick={handleSendInquiryButton}>
                {t('inquiry.button')}
              </Button>
              <DropDown
                onOutsideClick={handleClose}
                show={isDropDownShow}
                items={dropDownItems}>
                <Button onClick={handleDotsButton} size="small" variant="icon">
                  <BsThreeDots />
                </Button>
              </DropDown>
            </>
          ) : (
            <>
              {/* {user.role !== ROLES.CLIENT && user.role !== ROLES.SPECIALIST && (
                <Button
                  size="small"
                  variant="primary"
                  onClick={handleSubmitInquiryButton}>
                  {t('inquiry.submit.button')}
                </Button>
              )} */}
              <Button size="small" variant="secondary">
                <BsDownload
                  fill={colors.grey40}
                  style={{ width: '20px', height: '20px', marginRight: '8px' }}
                />
                {t('inquiry.download.button')}
              </Button>
            </>
          )}
        </JobContainer>
      )}
      <Notification
        show={isNotificationShow}
        text={t('inquiry.notification.text')}
        title={t('inquiry.notification.title')}
        buttonName={t('inquiry.notification.button')}
        onClick={closeNotification}
      />
      {job && (
        <GridTable
          rows={job.inquiry_payload}
          errors={inquiryValidationErrors}
          columnDefs={columnDefs}
          onAddItems={addRows}
          onUpdateItem={handleChangeJobs}
          onSelectItem={handleSelectItem}
          ref={ref}
          rowSelection="multiple"
          defaultRowsNumber={5}
          isEditable={isEditable}
        />
      )}
    </>
  );
};

export default Inquiry;
